import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { catchError, of } from 'rxjs';
import { ClientConfigService } from '../client-config/client-config.service';
import { ClientContextPersistenceService } from '../client-context-persistence/client-context-persistence.service';
import { ClientsService } from '../clients/clients.service';
import { EnvironmentService } from '../environment/environment.service';
import { LoggerService } from '../logger/logger.service';

/** Evita re-consultar el backend si el resolver ya validó el slug hace muy poco en esta misma navegación. */
const RECENT_VALIDATION_WINDOW_MS = 10 * 1000;

/**
 * Revalida en segundo plano si un webconfig sigue activo/visible, sin bloquear
 * el renderizado. Pensado para detectar que un cliente fue desactivado (o su
 * webconfig ocultado) mientras el usuario navega con datos cacheados.
 */
@Injectable({ providedIn: 'root' })
export class WebConfigAvailabilityService {
  private readonly clientsService = inject(ClientsService);
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly clientContextPersistence = inject(ClientContextPersistenceService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly logger = inject(LoggerService).forContext('WebConfigAvailabilityService');

  /**
   * Dispara la verificación de forma fire-and-forget. No devuelve nada ni
   * expone un Observable a propósito: el llamador no debe esperar el resultado.
   */
  verifyInBackground(slug: string | null | undefined): void {
    if (!slug || this.environmentService.isServer()) return;

    const cached = this.clientContextPersistence.get();
    const recentlyValidated =
      cached?.slug === slug && Date.now() - cached.validatedAt < RECENT_VALIDATION_WINDOW_MS;
    if (recentlyValidated) return;

    this.clientsService
      .validateWebConfig(slug)
      .pipe(
        catchError((error) => {
          // No pudimos verificar (red/timeout/5xx): no es evidencia de que el cliente
          // ya no exista, así que no debemos sacar al usuario del contexto actual.
          this.logger.warn(`Background webconfig check failed for "${slug}"`, error);
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) return;

        const isAvailable =
          response.exists && response.is_active && response.config?.show !== false;

        if (!isAvailable) {
          this.redirectToD2D(slug);
        }
      });
  }

  private redirectToD2D(slug: string): void {
    // Loop protection: si ya estamos en D2D (raíz) no hay nada que redirigir.
    if (this.router.url === '/d2d' || this.router.url === '') return;

    this.logger.warn(`Webconfig for "${slug}" is no longer active/public, redirecting to D2D`);

    this.clientContextPersistence.clear();
    this.clientConfigService.unSetConfig();

    this.messageService.add({
      severity: 'info',
      summary: this.translocoService.translate('webconfig.unavailableSummary'),
      detail: this.translocoService.translate('webconfig.unavailableDetail'),
      life: 4000,
    });

    this.router.navigate(['/d2d']);
  }
}
